/**
 * Puerta de datos SERVER-ONLY (REST) para pantallas sin espejo local-first
 * (caja, CRM, métricas…): con `networkMode: "offlineFirst"` una query sin red
 * queda `pending`+`paused` y el skeleton giraba PARA SIEMPRE. Esta puerta da
 * estados HONESTOS: skeleton solo mientras de verdad carga, estado offline
 * claro cuando no hay red, y error con reintento cuando el servidor falla.
 */
import {
  type Component,
  createSignal,
  type JSX,
  Match,
  onCleanup,
  onMount,
  Switch,
} from "solid-js";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { NotFoundState } from "./not-found-state";
import { Skeleton } from "./skeleton";

interface ServerDataGateProps {
  /** La query primaria aún no tiene resultado (isPending / isLoading). */
  pending: boolean;
  /** La query primaria falló (isError). */
  error: boolean;
  /** Reintento (refetch de la query primaria). */
  refetch: () => void;
  /** Skeleton a mostrar mientras carga con red (default: bloque genérico). */
  skeleton?: JSX.Element;
  children: JSX.Element;
}

/** Señal reactiva de red del navegador (suficiente para pantallas REST). */
function createOnlineSignal(): () => boolean {
  const [online, setOnline] = createSignal(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  onMount(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    globalThis.addEventListener("online", up);
    globalThis.addEventListener("offline", down);
    onCleanup(() => {
      globalThis.removeEventListener("online", up);
      globalThis.removeEventListener("offline", down);
    });
  });
  return online;
}

export const ServerDataGate: Component<ServerDataGateProps> = (props) => {
  const { t } = useI18n();
  const online = createOnlineSignal();

  return (
    <Switch>
      {/* Sin red y sin dato: estado offline honesto (no un spinner eterno). */}
      <Match when={(props.pending || props.error) && !online()}>
        <div data-testid="server-offline-state">
          <NotFoundState
            variant="offline"
            title={t(msg`This screen needs a connection`)}
            description={t(
              msg`This data lives on the server and isn't available offline. Reconnect to see it.`,
            )}
            secondaryAction={{
              label: t(msg`Retry`),
              onClick: () => {
                props.refetch();
              },
            }}
          />
        </div>
      </Match>
      {/* Con red pero el servidor falló: error + reintento. */}
      <Match when={props.error}>
        <div data-testid="server-error-state">
          <NotFoundState
            variant="offline"
            title={t(msg`Could not load this data`)}
            description={t(msg`The server didn't respond. Try again in a moment.`)}
            primaryAction={{
              label: t(msg`Retry`),
              onClick: () => {
                props.refetch();
              },
            }}
          />
        </div>
      </Match>
      {/* Cargando de verdad (con red). */}
      <Match when={props.pending}>
        {props.skeleton ?? <Skeleton class="h-40 w-full rounded-2xl" />}
      </Match>
      <Match when>{props.children}</Match>
    </Switch>
  );
};
